import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { EMAIL_1 } from "constants/configConstants";
import styles from "./GetHelpDesktop.module.scss";
import ContactCard from "./components/ContactCard";
import useTranslationHook from "hooks/useTranslationHook";
import config from "@Config/index";
import { AppState } from "@Store/index";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { FLOATING_BUTTON_ENTRY_POINTS, GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import { addLoadZendeskParamInURL } from "@Pages/PropertyPage/data/helper";
import LoginDispatcher from "@Components/Login/actions";
import openChatWidget from "helpers/openChatWidget";
import wrapperHOC from "@Utils/wrapperHOC";
import useWhitelabel from "hooks/useWhitelabel";
import useIsDesktop from "hooks/useIsDesktop";
import getWeComUrlBasedOnSource, {
  HK_WEIXIN_DWEB,
  HK_WEIXIN_MWEB,
} from "helpers/getWeComUrlBasedOnSource";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import useCountryContactInfo from "hooks/useCountryContactInfo";

const GetHelpDesktop = () => {
  const { t } = useTranslationHook();
  const { phoneContact } = useCountryContactInfo();

  const { isLFOneFilled, isUserLoggedIn } = useSelector(({ leadFormModal, appData }: AppState) => ({
    isLFOneFilled: leadFormModal.isLFOneFilled,
    isUserLoggedIn: appData.isUserLoggedIn,
  }));

  const { isWhitelabel } = useWhitelabel();

  const isZendeskAfterLogin = useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.ZENDESK_LOGIN_POPUP);
  const isDesktop = useIsDesktop();
  const isAmberCopilotEnabled = useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.AMBER_COPILOT);
  const [weChatSupportRedirectTo, setWeChatSupportRedirectTo] = useState(
    isDesktop ? HK_WEIXIN_DWEB : HK_WEIXIN_MWEB,
  );

  const dispatch = useDispatch();

  const handleZendeskCB = () => {
    if (isAmberCopilotEnabled) {
      openChatWidget({
        dispatch,
        isMobile: false,
        isDesktop: true,
        isAmberCopilotEnabled,
      });
      return;
    }
    if (isZendeskAfterLogin && !isUserLoggedIn && !isLFOneFilled) {
      addLoadZendeskParamInURL();
      const loginDispatcher = new LoginDispatcher(dispatch);
      loginDispatcher.showLoginModal({ openingSource: "zendesk" });
    } else {
      openChatWidget({
        dispatch,
        isMobile: false,
        isDesktop: true,
        isAmberCopilotEnabled,
        entryPoint: FLOATING_BUTTON_ENTRY_POINTS.PAGE_BUTTON,
      });
    }
  };

  useEffect(() => {
    const { URLDweb, URLMweb } = getWeComUrlBasedOnSource();
    setWeChatSupportRedirectTo(isDesktop ? URLDweb : URLMweb);
  }, [isDesktop]);

  return (
    <>
      <div className={styles.getHelpDesktopSection}>
        <div className={styles.largeScreenResponsive}>
          <div className={styles.headerLeft}>
            <h2 className={styles.title}>{t("home.gethelp.gethelptitle")}</h2>
            <h4 className={styles.subtite}>{t("home.gethelp.gethelpsubtitle")}</h4>
          </div>
          <div className={styles.contactCardsContainer}>
            <div className={styles.contactCardRow}>
              {!isWhitelabel && (
                <ContactCard
                  label="home.gethelp.contactcards.livechat"
                  iconKey="liveChat"
                  showTag
                  isDesktop
                  handleZendeskCB={handleZendeskCB}
                />
              )}
              {config.IS_CHINA_APP ? (
                <ContactCard
                  label="home.gethelp.contactcards.chatonwechat"
                  iconKey="weChat"
                  showTag
                  href={weChatSupportRedirectTo}
                  isDesktop
                />
              ) : (
                <ContactCard
                  label="home.gethelp.contactcards.chatonwhatsapp"
                  iconKey="whatsapp"
                  showTag
                  href="https://api.whatsapp.com/send/?phone=447888863490&text&type=phone_number&app_absent=0"
                  isDesktop
                />
              )}
            </div>
            <div className={styles.contactCardRow}>
              <ContactCard
                label={t("home.gethelp.contactcards.email")}
                iconKey="email"
                href={`mailto:${EMAIL_1}`}
                isDesktop
              />
              {config.IS_CHINA_APP ? (
                <></>
              ) : (
                <ContactCard
                  label={phoneContact?.label}
                  iconKey="phone"
                  href={phoneContact?.link}
                  isDesktop
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <div
        className={formatClassNames(styles.bottomBorder, isDesktop && styles.bottomBorderDesktop)}
      />
    </>
  );
};

export default wrapperHOC(GetHelpDesktop, {
  componentName: "GetHelpDesktop-Home",
  showForChina: true,
});
